import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Upload, FileText, CheckCircle2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminPDFs() {
  const [pdfs, setPdfs] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);

  const fetchPDFs = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/pdfs`);
      const data = await res.json();
      setPdfs(data.pdfs);
    } catch (error) {
      toast.error('Error al cargar PDFs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPDFs();
  }, [fetchPDFs]);

  const handleUpload = async (pdfKey, file) => {
    if (!file) return;
    
    if (!file.name.endsWith('.pdf')) {
      toast.error('Solo se permiten archivos PDF');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(pdfKey);

    try {
      const res = await fetch(`${BACKEND_URL}/api/pdfs/${pdfKey}`, {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (data.success) {
        toast.success('PDF actualizado correctamente', {
          description: `Archivo: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`,
        });
        fetchPDFs();
      } else {
        toast.error('Error al subir PDF');
      }
    } catch (error) {
      toast.error('Error al subir PDF', { description: error.message });
    } finally {
      setUploading(null);
    }
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const mb = bytes / 1024 / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: '#888' }}>Cargando...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '0', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#1a5c2e', marginBottom: '8px' }}>
          📁 Gestión de PDFs
        </h1>
        <p style={{ fontSize: '15px', color: '#666' }}>
          Administra las guías y manuales que se envían en los emails de alta
        </p>
      </div>

      {/* Info Card */}
      <Card style={{ marginBottom: '24px', background: '#f0f8f2', borderColor: '#cfe3d6' }}>
        <CardContent style={{ padding: '20px' }}>
          <p style={{ fontSize: '14px', color: '#1a5c2e', marginBottom: '8px', fontWeight: 600 }}>
            ℹ️ Información importante
          </p>
          <ul style={{ fontSize: '13px', color: '#555', lineHeight: 1.8, paddingLeft: '20px', margin: 0 }}>
            <li>Los PDFs se adjuntan automáticamente a los emails de bienvenida</li>
            <li><strong>Siempre se envían:</strong> Conexión a Trebol + Doble Factor</li>
            <li><strong>Condicionales:</strong> Enrolado Portátil/Móvil según el material asignado</li>
            <li>Los cambios se aplican inmediatamente a las nuevas altas</li>
          </ul>
        </CardContent>
      </Card>

      {/* PDFs Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
        {Object.entries(pdfs).map(([key, pdf]) => (
          <Card key={key} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardHeader style={{ paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  background: pdf.exists ? '#f0f8f2' : '#f5f5f5', 
                  borderRadius: '8px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <FileText style={{ width: '20px', height: '20px', color: pdf.exists ? '#2d8a4e' : '#999' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <CardTitle style={{ fontSize: '15px', color: '#1a5c2e', marginBottom: '4px' }}>
                    {pdf.display_name}
                  </CardTitle>
                  <CardDescription style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {pdf.exists ? (
                      <>
                        <CheckCircle2 style={{ width: '14px', height: '14px', color: '#3aaa62' }} />
                        <span>{formatSize(pdf.size)}</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle style={{ width: '14px', height: '14px', color: '#d97706' }} />
                        <span style={{ color: '#d97706' }}>No subido</span>
                      </>
                    )}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <Separator />
            <CardContent style={{ padding: '16px' }}>
              <label
                htmlFor={`upload-${key}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  background: uploading === key ? '#f5f5f5' : '#2d8a4e',
                  color: uploading === key ? '#888' : '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: uploading === key ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (uploading !== key) {
                    e.currentTarget.style.background = '#1a5c2e';
                  }
                }}
                onMouseLeave={(e) => {
                  if (uploading !== key) {
                    e.currentTarget.style.background = '#2d8a4e';
                  }
                }}
              >
                {uploading === key ? (
                  <>⏳ Subiendo...</>
                ) : (
                  <>
                    <Upload style={{ width: '16px', height: '16px' }} />
                    {pdf.exists ? 'Reemplazar PDF' : 'Subir PDF'}
                  </>
                )}
              </label>
              <input
                id={`upload-${key}`}
                type="file"
                accept=".pdf"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleUpload(key, file);
                  e.target.value = '';
                }}
                disabled={uploading === key}
              />
              {pdf.exists && (
                <p style={{ fontSize: '11px', color: '#888', marginTop: '8px', textAlign: 'center' }}>
                  Última actualización: {new Date().toLocaleDateString()}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Help Section */}
      <Card style={{ marginTop: '32px', background: '#f9fafb' }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px', color: '#1a5c2e' }}>❓ Ayuda</CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ fontSize: '13px', color: '#555', lineHeight: 1.8 }}>
            <p style={{ marginBottom: '12px' }}><strong>¿Cómo actualizar un PDF?</strong></p>
            <ol style={{ paddingLeft: '20px', margin: 0 }}>
              <li>Haz clic en "Subir PDF" o "Reemplazar PDF"</li>
              <li>Selecciona el archivo PDF desde tu ordenador</li>
              <li>El sistema lo subirá automáticamente</li>
              <li>Las nuevas altas recibirán el PDF actualizado</li>
            </ol>
            <p style={{ marginTop: '16px', padding: '12px', background: '#fff3cd', borderRadius: '6px', border: '1px solid #ffc107' }}>
              <strong>⚠️ Importante:</strong> Asegúrate de que los PDFs sean las versiones más recientes y estén correctamente nombrados.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
